import { useEffect } from 'react';

type SeoMeta = {
  title: string;
  description: string;
  lang: 'de' | 'en';
  /** Path without hash, e.g. "/de/for/projektmanager" */
  path: string;
  /** Corresponding path in other language */
  alternatePath: string;
  /** Optional JSON-LD structured data */
  jsonLd?: Record<string, unknown>;
};

const BASE = 'https://qtplan.com';

function setMetaTag(attr: string, key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (el) {
    el.setAttribute('content', content);
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.content = content;
    document.head.appendChild(el);
  }
}

function setLinkTag(rel: string, key: string, value: string, attr = 'hreflang') {
  let el = document.querySelector(`link[rel="${rel}"][${attr}="${key}"]`) as HTMLLinkElement | null;
  if (el) {
    el.href = value;
  } else {
    el = document.createElement('link');
    el.rel = rel;
    el.setAttribute(attr, key);
    el.href = value;
    document.head.appendChild(el);
  }
}

export function useSeoMeta({ title, description, lang, path, alternatePath, jsonLd }: SeoMeta) {
  useEffect(() => {
    // Title
    document.title = title;

    // Lang
    document.documentElement.lang = lang;

    // Description
    setMetaTag('name', 'description', description);

    // Canonical
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) canonical.href = `${BASE}/#${path}`;

    // Open Graph
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', `${BASE}/#${path}`);
    setMetaTag('property', 'og:locale', lang === 'de' ? 'de_DE' : 'en_US');
    setMetaTag('property', 'og:locale:alternate', lang === 'de' ? 'en_US' : 'de_DE');

    // Twitter
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);

    // Hreflang
    setLinkTag('alternate', 'de', `${BASE}/#${lang === 'de' ? path : alternatePath}`);
    setLinkTag('alternate', 'en', `${BASE}/#${lang === 'en' ? path : alternatePath}`);
    setLinkTag('alternate', 'x-default', `${BASE}/#${lang === 'en' ? path : alternatePath}`);

    // JSON-LD
    const existingScript = document.querySelector('script[data-seo-page]');
    if (jsonLd) {
      const json = JSON.stringify({ '@context': 'https://schema.org', ...jsonLd });
      if (existingScript) {
        existingScript.textContent = json;
      } else {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-page', 'true');
        script.textContent = json;
        document.head.appendChild(script);
      }
    } else if (existingScript) {
      existingScript.remove();
    }

    return () => {
      // Cleanup page-specific JSON-LD on unmount
      document.querySelector('script[data-seo-page]')?.remove();
    };
  }, [title, description, lang, path, alternatePath, jsonLd]);
}
